import moment from "moment";
import { actionLabel } from "./actions";

// The words the Automation screens show for what the API sends, as the app's
// app/automation/format.js words them; actions.js words what each device does.
// `lookup` comes from useAutomationFeature: lookup.name(applianceId) and
// lookup.device(applianceId).

export const TYPE_LABELS = {
  tap: "Tap to run",
  schedule: "Schedule",
  timer: "Timer",
  sun: "Sunrise/Sunset",
  rule: "Rule",
  location: "Location",
  nfc: "NFC tag",
  inching: "Inching",
  cycling: "Cycling",
};

// The Automation tab's filter chips, in order; inching and cycling share one.
export const FILTERS = [
  { key: "schedule", label: "Schedules", types: ["schedule"] },
  { key: "timer", label: "Timers", types: ["timer"] },
  { key: "sun", label: "Sunrise/sunset", types: ["sun"] },
  { key: "rule", label: "Rules", types: ["rule"] },
  { key: "location", label: "Location", types: ["location"] },
  { key: "nfc", label: "NFC", types: ["nfc"] },
  { key: "device", label: "Device behaviours", types: ["inching", "cycling"] },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Monday first, as the app lists days and the day circles show them.
export const WEEK = [1, 2, 3, 4, 5, 6, 0];

// [1,2,3,4,5] → "Mon–Fri"; days are 0 = Sunday ... 6 = Saturday.
export function daysText(days) {
  const set = Array.from(new Set(days || [])).sort();
  if (set.length === 7) return "Every day";
  if (set.length === 0) return "";
  const key = set.join(",");
  if (key === "1,2,3,4,5") return "Mon–Fri";
  if (key === "0,6") return "Weekends";
  const ordered = WEEK.filter((day) => set.includes(day));
  const start = WEEK.indexOf(ordered[0]);
  const inARow = ordered.length > 2 && ordered.every((day, i) => WEEK[start + i] === day);
  if (inARow) return `${DAY_NAMES[ordered[0]]}–${DAY_NAMES[ordered[ordered.length - 1]]}`;
  return ordered.map((day) => DAY_NAMES[day]).join(", ");
}

// "06:30" → "6:30 AM"
export function timeText(hhmm) {
  const time = moment(hhmm, "HH:mm", true);
  return time.isValid() ? time.format("h:mm A") : hhmm || "";
}

export function minutesText(minutes) {
  const value = Math.abs(minutes);
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function secondsText(seconds) {
  if (seconds < 60) return `${seconds} s`;
  if (seconds % 60 === 0) return minutesText(seconds / 60);
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

export function sunText(trigger) {
  const event = trigger?.event === "sunrise" ? "sunrise" : "sunset";
  const offset = trigger?.offsetMinutes || 0;
  if (!offset) return `At ${event}`;
  return `${minutesText(offset)} ${offset < 0 ? "before" : "after"} ${event}`;
}

// "today, 6:30 AM", "yesterday, 10:30 PM" or "29 Sep, 8:00 AM"
export function whenText(at) {
  const time = moment(at);
  if (!time.isValid()) return "";
  if (time.isSame(moment(), "day")) return `today, ${time.format("h:mm A")}`;
  if (time.isSame(moment().subtract(1, "day"), "day")) return `yesterday, ${time.format("h:mm A")}`;
  return time.format("D MMM, h:mm A");
}

// Section titles for the run history.
export function dayTitle(at) {
  const time = moment(at);
  if (time.isSame(moment(), "day")) return "Today";
  if (time.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return time.format(time.isSame(moment(), "year") ? "ddd, D MMM" : "D MMM YYYY");
}

// Runs split into days, in the order given (newest first from the API):
// [{ title, runs }] for a history list with a heading per day.
export function runsByDay(runs) {
  const days = [];
  runs.forEach((run) => {
    const title = dayTitle(run.at);
    const last = days[days.length - 1];
    if (last && last.title === title) last.runs.push(run);
    else days.push({ title, runs: [run] });
  });
  return days;
}

const FACT_WORDS = {
  isClosed: { true: "closes", false: "opens" },
  isOpen: { true: "opens", false: "closes" },
  isPresent: { true: "is triggered", false: "clears" },
};
const PRESENT_WORDS = {
  PIR_SENSOR: { true: "detects motion", false: "sees no motion" },
  GAS_SENSOR: { true: "detects gas", false: "clears" },
  SMOKE_DETECTOR: { true: "detects smoke", false: "clears" },
};

// "Main Door opens", "Hall motion detects motion"
export function factText(name, condition, deviceType) {
  const words = (condition?.fact === "isPresent" && PRESENT_WORDS[deviceType]) || FACT_WORDS[condition?.fact];
  const value = condition?.value;
  if (words && typeof value === "boolean" && ["equal", "notEqual"].includes(condition.operator)) {
    const truth = condition.operator === "equal" ? value : !value;
    return `${name} ${words[truth]}`;
  }
  if (condition?.fact === "timerange" && condition.value) {
    const range = `${timeText(condition.value.from)}–${timeText(condition.value.to)}`;
    return condition.operator === "notIn" ? `not ${range}` : range;
  }
  if (condition?.fact === "day" && Array.isArray(condition.value)) {
    return `${condition.operator === "notIn" ? "not " : ""}${daysText(condition.value)}`;
  }
  return `${name} ${condition?.fact} ${condition?.operator} ${JSON.stringify(condition?.value)}`;
}

// "Geyser on", in actions.js's words for the device
const actionText = (action, lookup) => `${lookup.name(action.applianceId)} ${actionLabel(action, lookup.device(action.applianceId))}`;

// "Geyser on · Hall light on" or "Geyser on · Hall light on · +3 more"
export function actionsText(actions, lookup) {
  const list = actions || [];
  if (!list.length) return "";
  const words = list.slice(0, 2).map((action) => actionText(action, lookup));
  if (list.length > 2) words.push(`+${list.length - 2} more`);
  return words.join(" · ");
}

// The one-line summary on cards: what starts it and what it does.
export function summaryText(automation, lookup) {
  const trigger = automation.trigger || {};
  const does = actionsText(automation.actions, lookup);
  const parts = [];
  switch (automation.type) {
    case "schedule":
      parts.push(daysText(trigger.days), timeText(trigger.time));
      break;
    case "sun":
      parts.push(daysText(trigger.days), sunText(trigger));
      break;
    case "timer":
      parts.push(trigger.runAt ? `Once, ${whenText(trigger.runAt)}` : "Once");
      break;
    case "rule": {
      const rule = factText(lookup.name(trigger.applianceId), trigger, lookup.device(trigger.applianceId)?.deviceType);
      const window = (automation.conditions?.all || []).find((c) => c.fact === "timerange");
      parts.push(rule);
      if (window) parts.push(factText("", window));
      break;
    }
    case "location":
      parts.push(`When ${trigger.person || "someone"} ${trigger.event === "leave" ? "leaves" : "arrives"}`);
      break;
    case "inching":
      if (automation.readOnly) parts.push("Set on the device");
      else parts.push(`Off ${secondsText(trigger.seconds)} after it turns on`);
      break;
    case "cycling":
      if (automation.readOnly) parts.push("Set on the device");
      else parts.push(`${minutesText(trigger.onMinutes)} on / ${minutesText(trigger.offMinutes)} off`);
      break;
    case "nfc":
      parts.push("NFC tag");
      break;
    default:
      parts.push(TYPE_LABELS[automation.type] || "");
  }
  if (does) parts.push(does);
  return parts.filter(Boolean).join(" · ");
}

// The "When" line on an automation's page.
export function triggerText(automation, lookup) {
  const trigger = automation.trigger || {};
  switch (automation.type) {
    case "tap":
      return "Someone taps Run";
    case "nfc":
      return "Someone taps their phone on its NFC tag";
    case "schedule":
      return `${daysText(trigger.days)} at ${timeText(trigger.time)}`;
    case "sun":
      return `${daysText(trigger.days)}, ${sunText(trigger).toLowerCase()}`;
    case "timer":
      return trigger.runAt ? `Once, ${whenText(trigger.runAt)}` : "Once";
    case "rule":
      return factText(lookup.name(trigger.applianceId), trigger, lookup.device(trigger.applianceId)?.deviceType);
    case "location":
      return `${trigger.person || "Someone"} ${trigger.event === "leave" ? "leaves" : "arrives"} (within ${trigger.radiusMeters || 200} m)`;
    default:
      return summaryText({ ...automation, actions: [] }, lookup);
  }
}

// A device in a condition is in a state ("Main Door is closed"), where a
// rule's trigger is an event ("opens").
const STATE_WORDS = {
  isClosed: { true: "is closed", false: "is open" },
  isPresent: { true: "is triggered", false: "is clear" },
};

function conditionText(condition, lookup) {
  if (!condition.applianceId) return factText("", condition).trim();
  const name = lookup.name(condition.applianceId);
  const words = STATE_WORDS[condition.fact];
  if (words && typeof condition.value === "boolean" && condition.operator === "equal") return `${name} ${words[condition.value]}`;
  return factText(name, condition, lookup.device(condition.applianceId)?.deviceType);
}

// The "Only if" lines: one per condition.
export function conditionLines(automation, lookup) {
  const conditions = automation.conditions || {};
  const line = (c) => conditionText(c, lookup);
  const all = (conditions.all || []).map(line);
  const any = (conditions.any || []).map(line);
  return any.length ? [...all, `Any of: ${any.join(", ")}`] : all;
}

// The "Then" lines: one per device.
export const actionLines = (automation, lookup) => (automation.actions || []).map((action) => actionText(action, lookup));

// The API says 'Skipped: its "only if" conditions weren't met.'; after
// "Skipped <when>" the prefix would repeat.
export const skipReason = (message) => (message || "").replace(/^Skipped:\s*/i, "");

// "Ran today, 6:30 AM", "Failed 8:00 AM · Water Level Controller was offline",
// or null before the first run.
export function lastRunText(lastRun) {
  if (!lastRun?.at) return null;
  const when = whenText(lastRun.at);
  const why = lastRun.message ? ` · ${skipReason(lastRun.message)}` : "";
  if (lastRun.status === "failed") return `Failed ${when}${why}`;
  if (lastRun.status === "skipped") return `Skipped ${when}${why}`;
  return `Ran ${when}`;
}

// What started a run: "Schedule", "Tap to run · by Pradeepa", "NFC · Bedside".
export function runSourceText(run) {
  const trigger = run.trigger || {};
  const by = trigger.by ? ` · by ${trigger.by}` : "";
  switch (trigger.source) {
    case "manual":
      return `${run.type === "tap" ? "Tap to run" : "Run now"}${by}`;
    case "nfc":
      return `NFC${trigger.tagName ? ` · ${trigger.tagName}` : ""}${by}`;
    case "location":
      return "Arriving or leaving";
    default:
      return TYPE_LABELS[run.type] || "";
  }
}

// "Ran today, 6:30 AM", or "Failed … · why" for a run in a list.
export function runWhenText(run) {
  const verb = { failed: "Failed", skipped: "Skipped" }[run.status] || "Ran";
  const why = run.status !== "ok" && run.message ? ` · ${skipReason(run.message)}` : "";
  return `${verb} ${whenText(run.at)}${why}`;
}

// What a run switched: "Geyser" or "Geyser +2".
export function runDevicesText(run, lookup) {
  const devices = run.devices || [];
  if (!devices.length) return "";
  const first = lookup.name(devices[0].applianceId);
  return devices.length > 1 ? `${first} +${devices.length - 1}` : first;
}
